import {
  Alert,
  Card,
  Center,
  Group,
  Loader,
  SegmentedControl,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';
import { BarChart } from '@mantine/charts';
import { Decimal } from 'decimal.js';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { ErrorMessage } from '../components/ErrorMessage.js';
import { useT } from '../i18n/useT.js';
import { PeriodPicker, toQuery } from '../components/PeriodPicker.js';
import type { Period } from '../components/PeriodPicker.js';
import { formatMoney } from '../format.js';
import { useBalanceSheet, useProfitAndLoss, useTrialBalance } from '../api/hooks.js';
import type { BalanceSheet, ProfitAndLoss, ReportLine, TrialBalance } from '../api/hooks.js';

/**
 * Rapporter — resultat, balance and saldobalance over the same ledger.
 *
 * Nothing here is stored: every figure is summed from `journal_lines` when it is
 * asked for, so a report is always the books as they are now rather than a
 * snapshot that can drift from them.
 *
 * A chart appears only where it says something a column of numbers does not —
 * revenue against expenses, where the comparison *is* the point. A trial
 * balance is a table, because its job is that two totals agree to the øre, and
 * no bar chart shows that.
 */

type ReportName = 'resultat' | 'balance' | 'saldobalance';

export function ReportsPage(): ReactNode {
  const t = useT();
  const [report, setReport] = useState<ReportName>('resultat');
  const [period, setPeriod] = useState<Period>({ from: null, to: null });

  return (
    <Stack gap="lg">
      <Title order={2}>{t('reports.title')}</Title>

      <Group align="flex-end" wrap="wrap" gap="lg">
        <SegmentedControl
          value={report}
          onChange={(value) => {
            setReport(value as ReportName);
          }}
          aria-label={t('reports.choose')}
          data={[
            { value: 'resultat', label: t('reports.tab.profitAndLoss') },
            { value: 'balance', label: t('reports.tab.balance') },
            { value: 'saldobalance', label: t('reports.tab.trial') },
          ]}
        />
        <PeriodPicker
          value={period}
          onChange={setPeriod}
          {...(report === 'balance' ? { toLabel: t('reports.asOf') } : {})}
        />
      </Group>

      {report === 'resultat' && <ProfitAndLossReport period={period} />}
      {report === 'balance' && <BalanceSheetReport period={period} />}
      {report === 'saldobalance' && <TrialBalanceReport period={period} />}
    </Stack>
  );
}

// -----------------------------------------------------------------------------
// Resultatopgørelse
// -----------------------------------------------------------------------------

function ProfitAndLossReport({ period }: { readonly period: Period }): ReactNode {
  const t = useT();
  const query = useProfitAndLoss(toQuery(period));

  if (query.isPending) {
    return <Loading />;
  }
  if (query.isError) {
    return <ErrorMessage error={query.error} />;
  }
  if (!query.isSuccess) {
    return null;
  }

  const report: ProfitAndLoss = query.data;
  const loss = new Decimal(report.result).isNegative();

  return (
    <Stack gap="md">
      <SimpleGrid cols={{ base: 1, sm: 3 }}>
        <Figure label={t('reports.revenue')} value={report.totalRevenue} />
        <Figure label={t('reports.expenses')} value={report.totalExpenses} />
        <Figure
          label={loss ? t('reports.loss') : t('reports.profit')}
          value={report.result}
          strong
          {...(loss ? { color: 'red' } : {})}
        />
      </SimpleGrid>

      {/*
        The one place a chart earns its keep on this page: the comparison is
        what the reader is here for, and two bars say it faster than two totals.
      */}
      <Card withBorder padding="md">
        <BarChart
          h={200}
          data={[
            {
              periode: t('reports.period'),
              [t('reports.revenue')]: new Decimal(report.totalRevenue).toNumber(),
              [t('reports.expenses')]: new Decimal(report.totalExpenses).toNumber(),
            },
          ]}
          dataKey="periode"
          series={[
            { name: t('reports.revenue'), color: 'teal.6' },
            { name: t('reports.expenses'), color: 'red.6' },
          ]}
          withLegend
        />
        {/*
          The chart is drawn from numbers, because a chart cannot be drawn from
          anything else. Every figure stated in text on this page comes from the
          decimal string instead, so the rounding a float can introduce never
          reaches a number anyone reads.
        */}
        <Text size="xs" c="dimmed" mt="xs">
          {t('reports.chartAdvisory')}
        </Text>
      </Card>

      <Alert color="gray" variant="light" title={t('reports.vatNotRevenueTitle')}>
        {t('reports.vatNotRevenue')}
      </Alert>

      <LineTable
        title={t('reports.revenue')}
        lines={report.revenue}
        total={report.totalRevenue}
      />
      <LineTable
        title={t('reports.expenses')}
        lines={report.expenses}
        total={report.totalExpenses}
      />
    </Stack>
  );
}

// -----------------------------------------------------------------------------
// Balance
// -----------------------------------------------------------------------------

function BalanceSheetReport({ period }: { readonly period: Period }): ReactNode {
  const t = useT();
  const query = useBalanceSheet(toQuery(period));

  if (query.isPending) {
    return <Loading />;
  }
  if (query.isError) {
    return <ErrorMessage error={query.error} />;
  }
  if (!query.isSuccess) {
    return null;
  }

  const report: BalanceSheet = query.data;

  return (
    <Stack gap="md">
      <SimpleGrid cols={{ base: 1, sm: 3 }}>
        <Figure label={t('reports.assets')} value={report.totalAssets} />
        <Figure label={t('reports.liabilities')} value={report.totalLiabilities} />
        <Figure label={t('reports.equity')} value={report.totalEquity} />
      </SimpleGrid>

      <BalanceCheck difference={report.difference} inBalance={report.inBalance} />

      <Alert color="gray" variant="light" title={t('reports.resultApartTitle')}>
        {t('reports.resultApart', { amount: formatMoney(report.resultForPeriod) })}
      </Alert>

      <LineTable
        title={t('reports.assets')}
        lines={report.assets}
        total={report.totalAssets}
      />
      <LineTable
        title={t('reports.liabilities')}
        lines={report.liabilities}
        total={report.totalLiabilities}
      />
      <LineTable
        title={t('reports.equity')}
        lines={report.equity}
        total={report.totalEquity}
      />
    </Stack>
  );
}

// -----------------------------------------------------------------------------
// Saldobalance
// -----------------------------------------------------------------------------

function TrialBalanceReport({ period }: { readonly period: Period }): ReactNode {
  const t = useT();
  const query = useTrialBalance(toQuery(period));

  if (query.isPending) {
    return <Loading />;
  }
  if (query.isError) {
    return <ErrorMessage error={query.error} />;
  }
  if (!query.isSuccess) {
    return null;
  }

  const report: TrialBalance = query.data;

  return (
    <Stack gap="md">
      <BalanceCheck difference={report.difference} inBalance={report.inBalance} />

      {report.rows.length === 0 ? (
        <Text c="dimmed">{t('reports.noEntries')}</Text>
      ) : (
        <Table.ScrollContainer minWidth={700}>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ width: 100 }}>{t('reports.col.account')}</Table.Th>
                <Table.Th>{t('reports.col.name')}</Table.Th>
                <Table.Th style={{ width: 140 }} className="money-cell">
                  {t('journal.col.debit')}
                </Table.Th>
                <Table.Th style={{ width: 140 }} className="money-cell">
                  {t('journal.col.credit')}
                </Table.Th>
                <Table.Th style={{ width: 140 }} className="money-cell">
                  {t('reports.balance')}
                </Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {report.rows.map((row) => (
                <Table.Tr key={row.accountId}>
                  <Table.Td>
                    <Text size="sm" ff="monospace">
                      {row.code}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{row.name}</Text>
                  </Table.Td>
                  <Table.Td className="money-cell">{formatMoney(row.debit)}</Table.Td>
                  <Table.Td className="money-cell">{formatMoney(row.credit)}</Table.Td>
                  <Table.Td className="money-cell">{formatMoney(row.balance)}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
            <Table.Tfoot>
              <Table.Tr>
                <Table.Td colSpan={2}>
                  <Text fw={600}>{t('reports.total')}</Text>
                </Table.Td>
                <Table.Td className="money-cell">
                  <Text fw={600}>{formatMoney(report.totalDebit)}</Text>
                </Table.Td>
                <Table.Td className="money-cell">
                  <Text fw={600}>{formatMoney(report.totalCredit)}</Text>
                </Table.Td>
                <Table.Td />
              </Table.Tr>
            </Table.Tfoot>
          </Table>
        </Table.ScrollContainer>
      )}
    </Stack>
  );
}

// -----------------------------------------------------------------------------
// Shared pieces
// -----------------------------------------------------------------------------

function Loading(): ReactNode {
  return (
    <Center p="xl">
      <Loader />
    </Center>
  );
}

/**
 * Whether the ledger agrees with itself.
 *
 * `inBalance` should always be true: the database refuses an unbalanced entry.
 * So a false here is not an ordinary bookkeeping mistake to note in a footer —
 * it means the books disagree with themselves, and it is shown as loudly as
 * that deserves.
 */
function BalanceCheck({
  difference,
  inBalance,
}: {
  readonly difference: string;
  readonly inBalance: boolean;
}): ReactNode {
  const t = useT();

  if (inBalance) {
    return (
      <Alert color="green" variant="light" title={t('reports.inBalanceTitle')}>
        <Text size="sm">
          {t('reports.inBalance', { difference: formatMoney(difference) })}
        </Text>
      </Alert>
    );
  }

  return (
    <Alert color="red" variant="light" title={t('reports.outOfBalanceTitle')}>
      <Text size="sm">
        {t('reports.outOfBalance', { difference: formatMoney(difference) })}
      </Text>
    </Alert>
  );
}

function Figure({
  label,
  value,
  strong,
  color,
}: {
  readonly label: string;
  readonly value: string;
  readonly strong?: boolean;
  readonly color?: string;
}): ReactNode {
  return (
    <Card withBorder padding="md">
      <Stack gap={4}>
        <Text size="sm" c="dimmed">
          {label}
        </Text>
        <Text
          className="money-cell"
          fw={strong === true ? 700 : 600}
          size="lg"
          {...(color === undefined ? {} : { c: color })}
        >
          {formatMoney(value)}
        </Text>
      </Stack>
    </Card>
  );
}

function LineTable({
  title,
  lines,
  total,
}: {
  readonly title: string;
  readonly lines: readonly ReportLine[];
  readonly total: string;
}): ReactNode {
  const t = useT();

  if (lines.length === 0) {
    return null;
  }

  return (
    <>
      <Text fw={500}>{title}</Text>
      <Table.ScrollContainer minWidth={520}>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th style={{ width: 100 }}>{t('reports.col.account')}</Table.Th>
              <Table.Th>{t('reports.col.name')}</Table.Th>
              <Table.Th style={{ width: 160 }} className="money-cell">
                {t('reports.amount')}
              </Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {lines.map((line) => (
              <Table.Tr key={line.code}>
                <Table.Td>
                  <Text size="sm" ff="monospace">
                    {line.code}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{line.name}</Text>
                </Table.Td>
                <Table.Td className="money-cell">{formatMoney(line.amount)}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
          <Table.Tfoot>
            <Table.Tr>
              <Table.Td colSpan={2}>
                <Text fw={600}>{t('reports.total')}</Text>
              </Table.Td>
              <Table.Td className="money-cell">
                <Text fw={600}>{formatMoney(total)}</Text>
              </Table.Td>
            </Table.Tr>
          </Table.Tfoot>
        </Table>
      </Table.ScrollContainer>
    </>
  );
}
